/**
 * Node visual para alterar fase de negócio CRM no Flow Builder
 * Exibe configuração de seleção do deal e fase destino
 * 
 * RELEASE: 1.0.0 - 2025-01-15 - Novo node CRM
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { ArrowRightLeft, Search, Variable, CheckCircle, XCircle, Zap } from 'lucide-react';
import { FlowNodeData, CrmChangePhaseConfig } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/**
 * Labels para exibição do filtro de status
 */
const STATUS_LABELS: Record<string, string> = {
  'open': 'Em Aberto',
  'won': 'Concluído (Ganho)',
  'lost': 'Perdido',
  'all': 'Todos os Status',
};

/**
 * Componente visual do node Alterar Fase CRM
 * Exibe estratégia de seleção do deal e fase destino
 */
export function CrmChangePhaseNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config as CrmChangePhaseConfig | undefined;
  
  // Verifica se node está configurado
  const isConfigured = config?.phase_id;
  
  // Determina texto da estratégia de seleção
  const getSelectionLabel = () => {
    if (!config) return null;
    if (config.deal_selection === 'from_trigger') return 'Do gatilho CRM';
    if (config.deal_selection === 'from_variable') {
      return `{{${config.deal_variable_name || 'deal_id'}}}`;
    }
    return 'Mais recente do contato';
  };

  return (
    <>
      {/* Toolbar flutuante para edição/exclusão */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={nodeData.onEdit as (() => void) | undefined}
        onDuplicate={(nodeData as any).onDuplicate as (() => void) | undefined}
        onDelete={nodeData.onDelete as (() => void) | undefined}
        onTestFromHere={(nodeData as any).onTestFromHere as (() => void) | undefined}
        isVisible={selected}
      />
      
      {/* Handle de entrada (topo) */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-violet-600 !border-2 !border-white"
      />

      {/* Corpo do node */}
      <div className={`
        min-w-[220px] max-w-[280px] bg-card rounded-lg border-2 shadow-lg
        ${selected ? 'border-violet-600 ring-2 ring-violet-600/30' : 'border-violet-500'}
        transition-all duration-200
      `}>
        {/* Header */}
        <div className="flex items-center gap-2 p-3 border-b border-border bg-violet-600/10 rounded-t-lg">
          <div className="p-1.5 rounded bg-violet-600">
            <ArrowRightLeft className="h-4 w-4 text-white" />
          </div>
          <span className="font-semibold text-sm">{nodeData.config?.nickname || 'Alterar Fase'}</span>
        </div>

        {/* Conteúdo */}
        <div className="p-3 space-y-2 text-xs">
          {isConfigured ? (
            <>
              {/* Seleção do Deal */}
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  {config?.deal_selection === 'from_trigger' ? (
                    <Zap className="h-3 w-3" />
                  ) : config?.deal_selection === 'from_variable' ? (
                    <Variable className="h-3 w-3" />
                  ) : (
                    <Search className="h-3 w-3" />
                  )}
                  <span className="font-medium">Deal:</span>
                </div>
                <p className="pl-4 font-medium truncate" title={getSelectionLabel() || ''}>
                  {getSelectionLabel()}
                </p>
                
                {/* Filtro de status (apenas para most_recent) */}
                {config?.deal_selection !== 'from_variable' && config?.deal_status_filter && (
                  <p className="pl-4 text-muted-foreground">
                    Status: <span className="font-medium">{STATUS_LABELS[config.deal_status_filter] || config.deal_status_filter}</span>
                  </p>
                )}
              </div>

              {/* Fase Destino */}
              <div className="pt-1 border-t border-border/50 space-y-1">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <span className="font-medium">Nova Fase:</span>
                </div>
                <div className="pl-4 flex items-center gap-1.5">
                  {config?.phase_color && (
                    <span 
                      className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                      style={{ backgroundColor: config.phase_color }}
                    />
                  )}
                  <span className="font-medium truncate" title={config?.phase_name}>
                    {config?.phase_name}
                  </span>
                  {config?.is_won && (
                    <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                  )}
                  {config?.is_lost && (
                    <XCircle className="h-3 w-3 text-red-500 flex-shrink-0" />
                  )}
                </div>
                
                {/* Pipeline (se disponível) */}
                {config?.pipeline_name && (
                  <p className="pl-4 text-muted-foreground truncate" title={config.pipeline_name}>
                    Pipeline: {config.pipeline_name}
                  </p>
                )}
              </div>
            </>
          ) : (
            <p className="text-muted-foreground italic text-center py-2">
              Clique para configurar
            </p>
          )}
        </div>
      </div>

      {/* Handle de saída (base) */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-violet-600 !border-2 !border-white"
      />
    </>
  );
}
